import React, { useMemo } from 'react';
import { Box, Typography, Paper, useTheme } from '@mui/material';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer, YAxis } from 'recharts';
import { format } from 'date-fns';
import MoodIcon from '@mui/icons-material/Mood';

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const moodMap = { 5: 'Great', 4: 'Good', 3: 'Okay', 2: 'Bad', 1: 'Awful' };
        return (
            <Paper elevation={3} sx={{ p: 1.5 }}>
                <Typography variant="caption" display="block">{`Date: ${format(new Date(payload[0].payload.timestamp), 'MMM d, yyyy')}`}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{`Mood: ${moodMap[payload[0].value] || 'N/A'}`}</Typography>
            </Paper>
        );
    }
    return null;
};

export default function MoodDashboard({ mood, onOpenModal }) {
    const theme = useTheme();

    const chartData = useMemo(() =>
        (mood?.series || []).map(point => ({
            timestamp: new Date(point.timestamp).getTime(),
            value: point.value
        })),
    [mood?.series]);

    if (!mood || !mood.series || mood.series.length < 2) return null;

    return (
        <Paper
            elevation={2}
            sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2, cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
            onDoubleClick={() => onOpenModal({ item_type: 'mood', title: 'Mood Analysis' }, mood)}
        >
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MoodIcon sx={{ mr: 1, color: 'text.secondary' }} /> Mood
            </Typography>
            <Box sx={{ height: 80 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                        <defs>
                            <linearGradient id="moodGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={theme.palette.success.main} stopOpacity={0.6}/>
                                <stop offset="50%" stopColor={theme.palette.warning.main} stopOpacity={0.3}/>
                                <stop offset="100%" stopColor={theme.palette.error.main} stopOpacity={0.5}/>
                            </linearGradient>
                        </defs>
                        <Tooltip content={<CustomTooltip />} />
                        <XAxis dataKey="timestamp" hide />
                        <YAxis domain={[0, 6]} hide />
                        <Area type="monotone" dataKey="value" stroke={theme.palette.primary.main} strokeWidth={2} fill="url(#moodGradient)" />
                    </AreaChart>
                </ResponsiveContainer>
            </Box>
        </Paper>
    );
}